// See EnrollmentVerification.spec.md for ENV-FR-001 through ENV-FR-007
// Step 6 of Enroll Hosts and the read only run details view: a role application table and a
// storage verification table, each behind a banner computed by enrollmentSelectors. Pure
// presentational, it re-renders whenever `run` changes (ENV-FR-007).
import { makeStyles } from '@material-ui/styles'
import Alert from 'core/components/Alert'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Card from 'core/elements/card'
import Text from 'core/elements/Text'
import type Theme from 'core/themes/model'
import {
  type Banner,
  combinedBanner,
  stepOneBanner,
  stepTwoBanner,
  stepTwoRows,
} from './enrollmentSelectors'
import type { EnrollmentRun } from './model'
import StepOneTable from './StepOneTable'
import StepSection from './StepSection'
import StepTwoTable from './StepTwoTable'

// Alert only has primary/success/warning/error. Banner also has 'neutral' for the two "nothing
// to do here" states (no backends, waiting for roles); the closest existing look is 'primary'.
const ALERT_VARIANT: Record<Banner['variant'], 'primary' | 'success' | 'warning' | 'error'> = {
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  error: 'error',
  neutral: 'primary',
}

const BANNER_ICON: Record<Banner['icon'], string> = {
  sync: 'arrows-rotate',
  check: 'check',
  warning: 'triangle-exclamation',
  error: 'circle-exclamation',
  clock: 'clock',
  ban: 'ban',
}

// SC-005: banners use role="status"; the icon is centered beside the title and message.
const BannerView = ({ banner, testId }: { banner: Banner; testId: string }) => (
  <div role="status" data-testid={testId}>
    <Alert
      variant={ALERT_VARIANT[banner.variant]}
      icon={
        // Light (outline) like every other alert icon in the app; only table status icons are solid.
        <FontAwesomeIcon size="md" spin={banner.icon === 'sync'}>
          {BANNER_ICON[banner.icon]}
        </FontAwesomeIcon>
      }
      title={
        <Text variant="caption1" component="div">
          {banner.title}
        </Text>
      }
      message={banner.message}
    />
  </div>
)

export interface EnrollmentVerificationProps {
  run: EnrollmentRun
  onRetry: (serviceHost: string) => void
}

export default function EnrollmentVerification({ run, onRetry }: EnrollmentVerificationProps) {
  const classes = useStyles()
  const combined = combinedBanner(run)
  const hasBackends = run.selection.backends.length > 0
  const rows = stepTwoRows(run)
  // ENV-FR-006: once combined, the per step banners underneath are not shown at all.
  const stepTwo = combined ? undefined : stepTwoBanner(run)

  return (
    <div className={classes.root}>
      {/* Same grey header card as steps 1 to 5 */}
      <StepSection
        title="Verification"
        description="Roles are applied to each host, then each host's storage connection is checked."
      />
      <BannerView banner={combined ?? stepOneBanner(run)} testId="enrollment-roles-banner" />
      <Card title="Step 1: Role Application">
        <StepOneTable run={run} />
      </Card>
      {stepTwo && <BannerView banner={stepTwo} testId="enrollment-storage-banner" />}
      {/* ENV-FR-003 edge case: no backends selected means the table never renders. */}
      {hasBackends && (
        <Card title="Step 2: Storage Verification">
          <StepTwoTable rows={rows} onRetry={onRetry} />
        </Card>
      )}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'grid',
    gap: theme.spacing(2),
  },
}))
